import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { createPortal } from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import Link from '@material-ui/core/Link';

import Button from '@quanterix-ui/core/Button';

import { UseMalwareScanResult } from 'src/utils/hooks';

import { TerminatedBy } from '../../typings';

import { useStyles } from './styles';

interface Props {
  isCancelDialogOpen: boolean;
  isMaterialsInfoStepActive: boolean;
  isMalwareScanCompleted: UseMalwareScanResult['isCompleted'];
  totalScannedFilesCount: UseMalwareScanResult['totalScannedFilesCount'];
  filesWithMalware: UseMalwareScanResult['temporaryFilesWithMalware'];
  cleanFilesWithMalware: () => void;
  onLinkClick: (terminatedReason?: TerminatedBy) => void;
}

const MalwareScanDialog: FC<Props> = ({
  isCancelDialogOpen,
  isMaterialsInfoStepActive,
  isMalwareScanCompleted,
  totalScannedFilesCount,
  filesWithMalware,
  cleanFilesWithMalware,
  onLinkClick,
}) => {
  const classes = useStyles();

  const { formatMessage } = useIntl();

  const [isOpen, setIsOpen] = useState(false);

  const modalRootElement = useRef<HTMLElement | null>(null);

  const hasFilesWithMalware = useMemo(
    () => filesWithMalware.length > 0,
    [filesWithMalware]
  );

  const isAllFilesHasMalware = useMemo(() => {
    return filesWithMalware.length === totalScannedFilesCount;
  }, [filesWithMalware, totalScannedFilesCount]);

  const filesWithMalwareToShow = useMemo(() => {
    if (hasFilesWithMalware && !isAllFilesHasMalware) {
      const filesWithCorrectNaming = filesWithMalware.reduce<string[]>(
        (base, nextFile) => {
          const fileNameParts = nextFile.split('/');
          const fileName = fileNameParts[fileNameParts.length - 1];

          return [...base, fileName];
        },
        []
      );

      return filesWithCorrectNaming;
    }

    return undefined;
  }, [hasFilesWithMalware, isAllFilesHasMalware, filesWithMalware]);

  const isMalwareFoundInAllFilesOnStepWithFilesUploader = useMemo(() => {
    return isMaterialsInfoStepActive && isAllFilesHasMalware;
  }, [isMaterialsInfoStepActive, isAllFilesHasMalware]);

  const isMalwareFoundInAllFilesOnDifferentStep = useMemo(() => {
    return !isMaterialsInfoStepActive && isAllFilesHasMalware;
  }, [isMaterialsInfoStepActive, isAllFilesHasMalware]);

  const isMalwareFoundInSomeFilesOnStepWithFilesUploader = useMemo(() => {
    return (
      isMaterialsInfoStepActive && hasFilesWithMalware && !isAllFilesHasMalware
    );
  }, [isMaterialsInfoStepActive, hasFilesWithMalware, isAllFilesHasMalware]);

  const isMalwareFoundInSomeFilesOnDifferentStep = useMemo(() => {
    return (
      !isMaterialsInfoStepActive && hasFilesWithMalware && !isAllFilesHasMalware
    );
  }, [isMaterialsInfoStepActive, hasFilesWithMalware, isAllFilesHasMalware]);

  useEffect(() => {
    // NOTE: document.createElement('div') - is for testing purpose
    modalRootElement.current =
      (document.getElementById('modal-root') as HTMLDivElement) ||
      document.createElement('div');
  }, []);

  useEffect(() => {
    if (!isCancelDialogOpen && hasFilesWithMalware && isMalwareScanCompleted) {
      setIsOpen(true);
    }
  }, [isCancelDialogOpen, hasFilesWithMalware, isMalwareScanCompleted]);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleCloseWithCleanUp = () => {
    cleanFilesWithMalware();
    handleClose();
  };

  const handleLinkClick = () => {
    if (isMalwareFoundInAllFilesOnDifferentStep) {
      onLinkClick('error');
    }

    if (isMalwareFoundInSomeFilesOnDifferentStep) {
      onLinkClick('warning');
    }

    handleClose();
  };

  if (!modalRootElement.current) {
    return null;
  }

  const filesWithMalwareToShowAsString = filesWithMalwareToShow?.join(', ');
  const wasOrWereString =
    filesWithMalwareToShow && filesWithMalwareToShow.length > 1
      ? formatMessage({
          id: 'page.accelerator_form.malware_scan.dialog.they_were',
        })
      : formatMessage({
          id: 'page.accelerator_form.malware_scan.dialog.it_was',
        });

  return createPortal(
    <Dialog classes={{ paper: classes.paper }} open={isOpen}>
      <DialogTitle className={classes.title}>
        {(isMalwareFoundInAllFilesOnStepWithFilesUploader ||
          isMalwareFoundInAllFilesOnDifferentStep) && (
          <FormattedMessage id="page.accelerator_form.malware_scan.dialog.malware_found_in_all_files" />
        )}
        {isMalwareFoundInSomeFilesOnStepWithFilesUploader && (
          <FormattedMessage
            id="page.accelerator_form.malware_scan.dialog.malware_found_in_few_files_on_same_step"
            values={{ filesWithMalwareToShowAsString, wasOrWereString }}
          />
        )}
        {isMalwareFoundInSomeFilesOnDifferentStep && (
          <FormattedMessage
            id="page.accelerator_form.malware_scan.dialog.malware_found_in_few_files_on_diff_step"
            values={{ filesWithMalwareToShowAsString, wasOrWereString }}
          />
        )}
      </DialogTitle>
      {(isMalwareFoundInAllFilesOnDifferentStep ||
        isMalwareFoundInSomeFilesOnDifferentStep) && (
        <DialogContent className={classes.content}>
          <DialogContentText>
            <Link
              className={classes.link}
              variant="body2"
              color="inherit"
              onClick={handleLinkClick}
            >
              <FormattedMessage id="page.accelerator_form.malware_scan.dialog.return_to_step_with_malware_scan" />
            </Link>
          </DialogContentText>
        </DialogContent>
      )}
      {!isMalwareFoundInAllFilesOnDifferentStep && (
        <DialogActions className={classes.actions}>
          {isMalwareFoundInAllFilesOnStepWithFilesUploader && (
            <Button onClick={handleClose}>
              <FormattedMessage id="app.button.ok" />
            </Button>
          )}
          {isMalwareFoundInSomeFilesOnStepWithFilesUploader && (
            <Button onClick={handleCloseWithCleanUp}>
              <FormattedMessage id="app.button.ok" />
            </Button>
          )}
          {isMalwareFoundInSomeFilesOnDifferentStep && (
            <Button onClick={handleCloseWithCleanUp}>
              <FormattedMessage id="app.button.yes_continue" />
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>,
    modalRootElement.current
  );
};

export default MalwareScanDialog;
